import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { PublicClientApplication, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { useQueryClient } from '@tanstack/react-query';
import { getRedirectUri } from '../config';
import { isE2EAdminUserMode, isE2ETestMode } from '../utils/e2e-test-mode';
import {
  broadcastAuthLogout,
  clearAuthLogoutMarker,
  hasAuthLogoutMarker,
  shouldRestoreMsalSession,
  subscribeToAuthLogout,
} from '../utils/auth-session-sync';

// Helper function to get redirect URI (reads from config.js, env var, or defaults to window.location.origin)
const getRedirectUriValue = () => getRedirectUri();

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_ID_CLIENT_ID || '',
    authority: import.meta.env.VITE_ENTRA_ID_AUTHORITY ||
      `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_ID_TENANT_ID || ''}`,
    // Use redirect URI from config.js, env var, or default to current origin
    // This allows runtime configuration without rebuilding
    redirectUri: getRedirectUriValue(),
  },
  cache: {
    // localStorage is shared across tabs so sign-out in one tab can clear the session (ECU-2993)
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: false,
  },
};

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

interface AuthContextType {
  account: AccountInfo | null;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const activeAccountIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const e2eTestMode = isE2ETestMode();

  const clearCachedPersona = useCallback((): void => {
    queryClient.removeQueries({ queryKey: ['auth-me'] });
  }, [queryClient]);

  /**
   * Drop the active account and in-memory MSAL cache for this tab. setActiveAccount(null)
   * alone leaves getAllAccounts() populated, so a later login that clears the logout
   * marker can restore the previous user on focus.
   */
  const evictMsalSession = useCallback((): void => {
    msalInstance.setActiveAccount(null);
    void msalInstance.clearCache();
  }, []);

  /**
   * Adopt the MSAL account for this tab. Any cached /auth/me persona is dropped
   * when the signed-in identity changes, so a peer tab cannot reuse the previous
   * user's Support/Admin flags after a cross-tab sign-out and new sign-in.
   */
  const adoptAccount = useCallback(
    (nextAccount: AccountInfo | null): void => {
      const nextAccountId = nextAccount?.homeAccountId ?? null;
      if (nextAccountId !== activeAccountIdRef.current) {
        clearCachedPersona();
      }
      activeAccountIdRef.current = nextAccountId;
      setAccount(nextAccount);
      setIsAuthenticated(Boolean(nextAccount));
    },
    [clearCachedPersona],
  );

  useEffect(() => {
    // Bypass authentication in E2E test mode
    if (e2eTestMode) {
      const e2eUsername = isE2EAdminUserMode()
        ? ((typeof window !== 'undefined' && window.localStorage.getItem('E2E_USER_EMAIL')) || 'e2e-admin@example.com')
        : ((typeof window !== 'undefined' && window.localStorage.getItem('E2E_USER_EMAIL')) || 'e2e-test@example.com');
      setAccount({
        homeAccountId: 'e2e-test-account',
        localAccountId: 'e2e-test-account',
        environment: 'e2e-test',
        tenantId: 'e2e-test-tenant',
        username: e2eUsername,
        name: 'E2E Test User',
      } as AccountInfo);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    // Initialize MSAL and handle redirect
    const initializeMsal = async () => {
      if (initializedRef.current) {
        return;
      }
      initializedRef.current = true;

      try {
        await msalInstance.initialize();

        // Handle redirect promise
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          clearAuthLogoutMarker();
          adoptAccount(response.account);
        } else {
          const accounts = msalInstance.getAllAccounts();
          if (shouldRestoreMsalSession(hasAuthLogoutMarker(), accounts.length)) {
            adoptAccount(accounts[0]);
          } else {
            adoptAccount(null);
          }
        }
      } catch (error) {
        console.error('Error initializing MSAL:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeMsal();
  }, [e2eTestMode, adoptAccount]);

  // Update account state when window regains focus (e.g., after redirect)
  useEffect(() => {
    // Skip focus handler in E2E test mode
    if (e2eTestMode) {
      return;
    }

    const handleFocus = () => {
      const accounts = msalInstance.getAllAccounts();
      if (shouldRestoreMsalSession(hasAuthLogoutMarker(), accounts.length)) {
        adoptAccount(accounts[0]);
      } else {
        adoptAccount(null);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [e2eTestMode, adoptAccount]);

  useEffect(() => {
    if (e2eTestMode) {
      return;
    }

    return subscribeToAuthLogout(() => {
      evictMsalSession();
      clearCachedPersona();
      activeAccountIdRef.current = null;
      setAccount(null);
      setIsAuthenticated(false);
    });
  }, [e2eTestMode, evictMsalSession, clearCachedPersona]);

  const login = async () => {
    // In E2E test mode, no-op (already authenticated)
    if (e2eTestMode) {
      return;
    }

    try {
      // Request standard scopes for authentication (ID token will contain group claims)
      // Explicitly pass redirectUri to override any cached localhost value from sessionStorage
      await msalInstance.loginRedirect({
        scopes: ['openid', 'profile', 'email'],
        redirectUri: getRedirectUriValue(),
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      broadcastAuthLogout();
      evictMsalSession();
      clearCachedPersona();
      activeAccountIdRef.current = null;
      setAccount(null);
      setIsAuthenticated(false);
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: getRedirectUriValue(),
      });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    // In E2E test mode, return null (backend will bypass auth)
    if (e2eTestMode) {
      return null;
    }

    if (!account) {
      return null;
    }

    try {
      // Get ID token with openid scopes (contains group claims if configured)
      const response: AuthenticationResult = await msalInstance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: account,
      });

      // Use ID token for authentication (it has the client ID as audience and contains group claims)
      if (response.idToken) {
        return response.idToken;
      }

      // Fall back to access token if ID token not available
      if (response.accessToken) {
        return response.accessToken;
      }

      return null;
    } catch (error: any) {
      console.error('Error acquiring ID token:', error);

      // If silent token acquisition fails, try interactive
      if (error.name === 'InteractionRequiredError' || error.name === 'BrowserAuthError') {
        try {
          // Explicitly pass redirectUri to override any cached localhost value from sessionStorage
          await msalInstance.acquireTokenRedirect({
            scopes: ['openid', 'profile', 'email'],
            account: account,
            redirectUri: getRedirectUriValue(),
          });
          // Token will be available after redirect
          return null;
        } catch (redirectError) {
          console.error('Error acquiring token via redirect:', redirectError);
          return null;
        }
      }

      return null;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        account,
        isAuthenticated,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

